import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {useNavigate} from 'react-router-dom';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {PageHeader} from '@common-web/components/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';

import {Plus, Loader2, AlertCircle, UserPlus} from 'lucide-react';

import {useAuthStore} from '@common-web/stores/authStore';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {StateSelect} from '@client/components/StateSelect';

const createProspectSchema = z.object({
  clientId: z.string().optional(),
  companyName: z.string().min(1, 'Company name is required'),
  state: z.string().optional(),
  websiteUrl: z.string().optional(),
  estimatedFtEmployees: z.coerce.number().int().min(0).optional(),
  estimatedPtEmployees: z.coerce.number().int().min(0).optional(),
  estimatedStartDate: z.string().optional(),
  averageEmployeeSalary: z.coerce.number().min(0).optional(),
  assignedBrokerId: z.string().optional(),
});

type CreateProspectForm = z.infer<typeof createProspectSchema>;

export function CreateProspect() {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());
  const navigate = useNavigate();

  const form = useForm<CreateProspectForm>({
    resolver: zodResolver(createProspectSchema),
    defaultValues: {
      companyName: '',
      state: undefined,
      websiteUrl: '',
      estimatedFtEmployees: undefined,
      estimatedPtEmployees: undefined,
      estimatedStartDate: '',
      averageEmployeeSalary: undefined,
      assignedBrokerId: undefined,
    },
  });

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: isInternalUser,
    refetchOnWindowFocus: false,
  });

  const brokersQuery = trpc.clients.prospects.listBrokers.useQuery(undefined, {
    enabled: !isBrokerUser,
    refetchOnWindowFocus: false,
  });

  const createMutation = trpc.clients.prospects.create.useMutation({
    onSuccess: (data) => {
      const basePath = isBrokerUser ? '/broker/prospects' : '/prospects';
      navigate(`${basePath}/${data.id}`);
    },
    onError: createMutationErrorHandler('Failed to create prospect'),
  });

  const onSubmit = async (data: CreateProspectForm) => {
    await createMutation.mutateAsync({
      ...data,
      estimatedFtEmployees: data.estimatedFtEmployees ?? undefined,
      estimatedPtEmployees: data.estimatedPtEmployees ?? undefined,
      averageEmployeeSalary: data.averageEmployeeSalary ?? undefined,
      estimatedStartDate: data.estimatedStartDate ?? undefined,
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='New Prospect'
          description='Add a new prospect to your pipeline'
          icon={<UserPlus className='h-6 w-6' />}
        />

        {/* Form */}
        <div className='px-8 py-6 max-w-3xl mx-auto w-full'>
          <Card className='border-gray-200 shadow-sm'>
            <CardHeader className='bg-gray-50 border-b'>
              <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                <Plus className='h-5 w-5 text-brand-blue' />
                Prospect Details
              </CardTitle>
            </CardHeader>
            <CardContent className='p-6'>
              <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
                {/* Client Selector - Internal only */}
                {isInternalUser && (
                  <div>
                    <Label className='text-brand-navy'>Client *</Label>
                    {clientsQuery.isLoading ? (
                      <Skeleton className='h-10 w-full mt-1' />
                    ) : (
                      <Select
                        value={form.watch('clientId') || ''}
                        onValueChange={(value) => form.setValue('clientId', value)}
                      >
                        <SelectTrigger className='border-gray-200'>
                          <SelectValue placeholder='Select a client' />
                        </SelectTrigger>
                        <SelectContent>
                          {clientsQuery.data?.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Company Name */}
                <div>
                  <Label className='text-brand-navy'>Company Name *</Label>
                  <Input
                    placeholder='Enter company name'
                    {...form.register('companyName')}
                    className='border-gray-200'
                  />
                  {form.formState.errors.companyName && (
                    <p className='text-sm text-red-600 mt-1'>
                      {form.formState.errors.companyName.message}
                    </p>
                  )}
                </div>

                {/* State */}
                <div>
                  <Label className='text-brand-navy'>State</Label>
                  <StateSelect
                    value={form.watch('state') || ''}
                    onValueChange={(value) => form.setValue('state', value)}
                    className='border-gray-200'
                  />
                </div>

                {/* Website */}
                <div>
                  <Label className='text-brand-navy'>Website URL</Label>
                  <Input
                    placeholder='https://example.com'
                    {...form.register('websiteUrl')}
                    className='border-gray-200'
                  />
                </div>

                {/* Employee counts */}
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <Label className='text-brand-navy'>Est. FT Employees</Label>
                    <Input
                      type='number'
                      placeholder='0'
                      {...form.register('estimatedFtEmployees')}
                      className='border-gray-200'
                    />
                  </div>
                  <div>
                    <Label className='text-brand-navy'>Est. PT Employees</Label>
                    <Input
                      type='number'
                      placeholder='0'
                      {...form.register('estimatedPtEmployees')}
                      className='border-gray-200'
                    />
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <Label className='text-brand-navy'>Estimated Start Date</Label>
                  <Input
                    type='date'
                    {...form.register('estimatedStartDate')}
                    className='border-gray-200'
                  />
                </div>

                {/* Average Salary */}
                <div>
                  <Label className='text-brand-navy'>Average Employee Salary</Label>
                  <Input
                    type='number'
                    step='0.01'
                    placeholder='0.00'
                    {...form.register('averageEmployeeSalary')}
                    className='border-gray-200'
                  />
                </div>

                {/* Broker Assignment */}
                {!isBrokerUser && brokersQuery.data && brokersQuery.data.length > 0 && (
                  <div>
                    <Label className='text-brand-navy'>Assign Broker (Optional)</Label>
                    <Select
                      value={form.watch('assignedBrokerId') || 'none'}
                      onValueChange={(value) =>
                        form.setValue('assignedBrokerId', value === 'none' ? undefined : value)
                      }
                    >
                      <SelectTrigger className='border-gray-200'>
                        <SelectValue placeholder='No broker assigned' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='none'>No broker assigned</SelectItem>
                        {brokersQuery.data.map((broker) => (
                          <SelectItem key={broker.id} value={broker.id}>
                            {broker.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Submit */}
                <Button
                  type='submit'
                  disabled={createMutation.isPending}
                  className='w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm'
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Creating...
                    </>
                  ) : (
                    'Create Prospect'
                  )}
                </Button>

                {!!createMutation.error && (
                  <Alert variant='destructive'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{createMutation.error.message}</AlertDescription>
                  </Alert>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
